import { useCallback, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { ApiErrors } from '../../enums';
import { useLazyGetPixKeyDetailsQuery, useLazyGetPixKeyDetailsV3Query } from '../../services/pixKeysApi';
export function useValidatePixKeyDetails() {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBaasV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const [triggerV2] = useLazyGetPixKeyDetailsQuery();
    const [triggerV3] = useLazyGetPixKeyDetailsV3Query();
    const [isValidating, setIsValidating] = useState(false);
    const [pixKeyDetails, setPixKeyDetails] = useState(null);
    const [error, setError] = useState(null);
    const [showInvalidKeyError, setShowInvalidKeyError] = useState(false);
    const validatePixKey = useCallback(async (key) => {
        if (!key.trim()) {
            setError(t('pix-transfer.key-required'));
            return null;
        }
        setIsValidating(true);
        setError(null);
        setShowInvalidKeyError(false);
        setPixKeyDetails(null);
        const trigger = isBaasV2Enabled ? triggerV3 : triggerV2;
        try {
            const result = await trigger({ key }).unwrap();
            const details = result.result || result;
            setPixKeyDetails(details);
            return details;
        }
        catch (error) {
            const errorObj = error;
            if ((errorObj.status === 404 &&
                (errorObj.result?.error === ApiErrors.PIX_KEY_VALIDATE_NOT_FOUND ||
                    errorObj.result?.error === ApiErrors.PARTNER_INTEGRATION_ERROR)) ||
                errorObj.status === 500) {
                setShowInvalidKeyError(true);
            }
            else {
                setError(t('pix-transfer.validation-error'));
            }
            return null;
        }
        finally {
            setIsValidating(false);
        }
    }, [isBaasV2Enabled, triggerV2, triggerV3, t]);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    const clearInvalidKeyError = useCallback(() => {
        setShowInvalidKeyError(false);
    }, []);
    return {
        isValidating,
        pixKeyDetails,
        error,
        showInvalidKeyError,
        validatePixKey,
        clearError,
        clearInvalidKeyError,
    };
}
